import React, { useRef, useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { cloneDeep } from 'lodash-es';
import { useIntl, useDispatch } from 'umi';
import { Segment } from 'tea-component';
import { Bubble } from '@home/components/TeaComponents';
import CasualCompetitorsFilter from './components/Filter';
import CategoryOverview, { ICategoryOverviewInstance } from './components/CategoryOverview';
import KeyGames, { IKeyGamesInstance } from './components/KeyGames';
import TopPublishersV2, { ITopPublishersInstance } from './components/TopPublishersV2';
import CasualCompetitorsFooter from './components/Footer';
import useConfigureTexts from '@intelligence/hooks/useConfigureTexts';
import useMarketFilterApi, { MarketSpecailType } from '@intelligence/hooks/useMarketSelector';
import useFilterUrlLinkage from '@intelligence/hooks/useFilterUrlLinkage';
import useSetQueryDefinition, { useQueryDefinition } from '@intelligence/hooks/useQueryDefinition';
import useEarliesTimeHasData from '@intelligence/hooks/useEarliesTimeHasData';
import useElementLocale from './_config/text';
import { buriedPoint } from '@home/utils/buriedPoint';
import { REVENUE_SCOPE_VALUES } from '@intelligence/utils/revenueScope';
// 工具方法
import { getTranslatedText, getValidDateType, getLastMonthRange, resolveGenreTabs, buildParams } from './utils';
// 常量
import { LANGUAGE_CN } from '@home/_config/config';
import * as filterKeyMap from '@intelligence/components/Filter/FilterBase/_config';
import { hasDataTimerParams } from '@intelligence/utils/timerDefautParams';
import { CONFIGURE_TEXTS_MODEL_NAMESPACE } from '@intelligence/models/configureTexts';
import {
  CASUAL_COMPETITORS_FILTER_URL_CONFIG,
  DEFAULT_FILTER_VALUE,
  CASUAL_COMPETITORS_REVENUE_SCOPE_FILTER_URL_CONFIG,
} from './_config/filterUrlConfig';
import {
  ENTITY_TYPE,
  CASUAL_COMPETITORS_PAGE_LOG_CFG,
  TAB_OPTIONS_FIRST,
  TAB_BUBBLE_KEY,
  getSecondaryTabOptions,
  hasSecondaryGenreTabs,
  getDefaultSecondaryTabByPrimary,
} from './constants';

import type { IStudioInfo } from '../../type';
import type { ICasualCompetitorsDefaultGenre, ICasualCompetitorsParams } from './types';

import './index.scss';

interface ICasualCompetitorsProps {
  studioInfo?: IStudioInfo | null;
  defaultCompetitorsGenre?: ICasualCompetitorsDefaultGenre;
  showRevenueScopeSwitcher?: boolean;
  showFilterTipsText?: boolean;
  categoryKpiTipsI18n?: IRecord<string>;
  keyGamesTableTipsI18n?: IRecord<IRecord<string>>;
  topPublishersTableTipsI18n?: IRecord<IRecord<string>>;
  topPublisersKpiTipsI18n?: IRecord<{
    dau: string;
    iap: string;
    download: string;
  }>;
}

const SENSOR_TOWER_DATA_SOURCE = 'sensortower';

const CasualCompetitors = (props: ICasualCompetitorsProps) => {
  const {
    studioInfo,
    defaultCompetitorsGenre,
    showRevenueScopeSwitcher = false,
    showFilterTipsText = true,
    categoryKpiTipsI18n,
    keyGamesTableTipsI18n,
    topPublishersTableTipsI18n,
    topPublisersKpiTipsI18n,
  } = props;
  const dispatch = useDispatch();
  const { locale, formatMessage } = useIntl();
  const { formatElementMessage } = useElementLocale(locale);
  const { studio_code: studioCode } = studioInfo?.data?.studio_info || {};
  const initialGenreTabs = resolveGenreTabs(undefined, undefined, defaultCompetitorsGenre);
  const filterUrlConfig = useMemo(() => {
    if (!showRevenueScopeSwitcher) {
      return CASUAL_COMPETITORS_FILTER_URL_CONFIG;
    }

    return {
      ...CASUAL_COMPETITORS_FILTER_URL_CONFIG,
      ...CASUAL_COMPETITORS_REVENUE_SCOPE_FILTER_URL_CONFIG,
    };
  }, [showRevenueScopeSwitcher]);

  // 初始化公共筛选器数据（品类选项映射）
  useSetQueryDefinition({
    genreMap: {
      target: 'filter_genre',
      params: {
        entity_type: ['mobile'],
        need_filter: true,
      },
    },
  });

  // 获取地区选项数据（与Mobile TopCharts一致）
  const { filterLoading: regionLoading, regionOptions } = useMarketFilterApi({
    filterType: MarketSpecailType.common,
  });
  const { filterLoading: defaultTimeLoading, latestTimeTypeHasData } = useEarliesTimeHasData({
    filterType: hasDataTimerParams?.mobileActiveuser?.type,
  });

  // 获取品类选项映射（用于子品类翻译）
  const { genreOptionsMap } = useQueryDefinition();

  // 品类Tab Bubble提示文案
  const { configureTexts } = useConfigureTexts();

  // 品类Tab状态
  const [primaryTab, setPrimaryTab] = useState<string>(initialGenreTabs.primaryTab);
  const [secondaryTab, setSecondaryTab] = useState<string>(initialGenreTabs.secondaryTab);
  const shouldShowRevenueScopeSwitcher = showRevenueScopeSwitcher && primaryTab !== 'iaa';

  const getDefaultDateValue = (dateType: string): [moment.Moment, moment.Moment] => {
    const defaultDateValue = latestTimeTypeHasData?.[dateType];

    if (Array.isArray(defaultDateValue) && defaultDateValue.length === 2) {
      // const startDate = moment(defaultDateValue[0]);
      const endDate = moment(defaultDateValue[1]);
      const startDate = endDate.clone();
      if (startDate.isValid() && endDate.isValid()) {
        if (dateType === 'month') {
          startDate.startOf('month');
          endDate.endOf('month');
        }
        return [startDate, endDate];
      }
    }

    return getLastMonthRange();
  };

  // 筛选器value
  const [filterValue, setFilterValue] = useState<IRecord>(() => {
    const [startDate, endDate] = getLastMonthRange();
    return {
      ...cloneDeep(DEFAULT_FILTER_VALUE),
      [filterKeyMap.RANGE_TIME]: {
        type: 'month',
        value: [startDate, endDate],
      },
    };
  });

  // 品类大盘模块实例
  const categoryOverviewRef = useRef<ICategoryOverviewInstance>(null);

  // 重点游戏模块实例
  const keyGamesRef = useRef<IKeyGamesInstance>(null);

  // 头部厂商模块实例
  const topPublishersRef = useRef<ITopPublishersInstance>(null);

  // 用 useMemo 维护 currentParams
  const currentParams = useMemo<ICasualCompetitorsParams | null>(() => {
    return buildParams(primaryTab, secondaryTab, filterValue, shouldShowRevenueScopeSwitcher);
  }, [primaryTab, secondaryTab, filterValue, shouldShowRevenueScopeSwitcher]);

  // 调用子组件 update 方法
  const update = (params: ICasualCompetitorsParams) => {
    categoryOverviewRef.current?.update(params);
    keyGamesRef.current?.update(params);
    topPublishersRef.current?.update(params);
  };

  // URL参数同步
  const { isLoading, decodeObj, updateUrl } = useFilterUrlLinkage({
    needDecodeUrl: true,
    needUpdateUrl: true,
    // @ts-ignore
    config: filterUrlConfig,
    filterData: filterValue,
  });

  // 更新筛选器值并同步URL
  const handleFilterChange = (value: IRecord) => {
    setFilterValue(value);
    // @ts-ignore
    updateUrl?.(value, shouldShowRevenueScopeSwitcher ? filterUrlConfig : CASUAL_COMPETITORS_FILTER_URL_CONFIG);

    // 用新的筛选器值构建参数并调用 update
    const newParams = buildParams(primaryTab, secondaryTab, value, shouldShowRevenueScopeSwitcher);
    update(newParams);
  };

  // 品类Tab切换
  const handlePrimaryTabChange = (value: string) => {
    const newPrimaryTab = value;
    const newSecondaryTab = getDefaultSecondaryTabByPrimary(value);
    const nextShouldShowRevenueScopeSwitcher = showRevenueScopeSwitcher && newPrimaryTab !== 'iaa';
    setPrimaryTab(newPrimaryTab);
    setSecondaryTab(newSecondaryTab);

    // 更新URL
    const newFilterValue = {
      ...filterValue,
      primaryTab: value,
      secondaryTab: newSecondaryTab,
    };
    // @ts-ignore
    updateUrl?.(
      newFilterValue,
      // @ts-ignore
      nextShouldShowRevenueScopeSwitcher ? filterUrlConfig : CASUAL_COMPETITORS_FILTER_URL_CONFIG,
    );

    // 用新的 Tab 值构建参数并调用 update
    const newParams = buildParams(newPrimaryTab, newSecondaryTab, filterValue, nextShouldShowRevenueScopeSwitcher);
    update(newParams);
  };

  const handleSecondaryTabChange = (value: string) => {
    const nextShouldShowRevenueScopeSwitcher = showRevenueScopeSwitcher && primaryTab !== 'iaa';
    setSecondaryTab(value);
    // 更新URL
    const newFilterValue = {
      ...filterValue,
      secondaryTab: value,
    };
    // @ts-ignore
    updateUrl?.(
      newFilterValue,
      // @ts-ignore
      nextShouldShowRevenueScopeSwitcher ? filterUrlConfig : CASUAL_COMPETITORS_FILTER_URL_CONFIG,
    );

    // 用新的 Tab 值构建参数并调用 update
    const newParams = buildParams(primaryTab, value, filterValue, nextShouldShowRevenueScopeSwitcher);
    update(newParams);
  };

  const buildSegmentOption = (
    option?: { text: string; value: string },
    config?: { disableBubble?: boolean },
  ) => {
    const optionValue = option?.value || '';
    const label = getTranslatedText(formatMessage, option?.text);
    const bubbleKey = TAB_BUBBLE_KEY[optionValue];
    const bubbleContent =
      !config?.disableBubble && bubbleKey ? configureTexts?.[bubbleKey] : undefined;

    return {
      value: optionValue,
      text: bubbleContent ? (
        <Bubble placement="top" content={bubbleContent}>
          <span>{label}</span>
        </Bubble>
      ) : (
        <span>{label}</span>
      ),
    };
  };

  // 页面初始化 - 从URL参数恢复筛选器状态
  useEffect(() => {
    if (isLoading || defaultTimeLoading) {
      return;
    }

    // 解析URL参数
    const primaryTabFromUrl = decodeObj?.primaryTab;
    const secondaryTabFromUrl = decodeObj?.secondaryTab;
    const rangeTimeDecodeObj = decodeObj?.[filterKeyMap.RANGE_TIME];
    const regionDecodeObj = decodeObj?.[filterKeyMap.REGION];
    const platformDecodeObj = decodeObj?.[filterKeyMap.PLATFORM];
    const revenueScopeDecodeObj = decodeObj?.[filterKeyMap.REVENUE_SCOPE];

    // 更新Tab状态
    const resolvedGenreTabs = resolveGenreTabs(primaryTabFromUrl, secondaryTabFromUrl, defaultCompetitorsGenre);
    const finalPrimaryTab = resolvedGenreTabs.primaryTab;
    const finalSecondaryTab = resolvedGenreTabs.secondaryTab;
    const shouldEnableRevenueScopeOnInit = showRevenueScopeSwitcher && finalPrimaryTab !== 'iaa';
    setPrimaryTab(finalPrimaryTab);
    setSecondaryTab(finalSecondaryTab);

    // 更新筛选器状态
    const initialDateType = getValidDateType(rangeTimeDecodeObj?.type || filterValue[filterKeyMap.RANGE_TIME]?.type);
    const [defaultStartDate, defaultEndDate] = getDefaultDateValue(initialDateType);
    const newFilterValue = {
      ...filterValue,
      [filterKeyMap.RANGE_TIME]: {
        type: initialDateType,
        value: rangeTimeDecodeObj?.value || [defaultStartDate, defaultEndDate],
      },
      [filterKeyMap.REGION]: {
        ...filterValue[filterKeyMap.REGION],
        markets: regionDecodeObj?.markets || ['global'],
      },
      [filterKeyMap.PLATFORM]: {
        value: platformDecodeObj?.value || ['appstore', 'googleplay'],
      },
      [filterKeyMap.REVENUE_SCOPE]: {
        value: shouldEnableRevenueScopeOnInit
          ? revenueScopeDecodeObj?.value || REVENUE_SCOPE_VALUES.EXCLUDING
          : filterValue?.[filterKeyMap.REVENUE_SCOPE]?.value,
      },
    };

    setFilterValue(newFilterValue);

    // 页面初始化完成后，调用update
    const newParams = buildParams(finalPrimaryTab, finalSecondaryTab, newFilterValue, shouldEnableRevenueScopeOnInit);
    // @ts-ignore
    updateUrl?.(
      newFilterValue,
      // @ts-ignore
      finalPrimaryTab === 'iaa' ? CASUAL_COMPETITORS_FILTER_URL_CONFIG : filterUrlConfig,
    );
    update(newParams);
  }, [isLoading, defaultTimeLoading, latestTimeTypeHasData, defaultCompetitorsGenre, showRevenueScopeSwitcher]);

  // 埋点
  useEffect(() => {
    dispatch({
      type: `${CONFIGURE_TEXTS_MODEL_NAMESPACE}/getConfigureTexts`,
      payload: {
        prefix: 'home_studio_casual_competitors_genre_type_tips_*',
      },
    });

    buriedPoint({ ...CASUAL_COMPETITORS_PAGE_LOG_CFG, uid: studioCode });
  }, [studioCode]);

  return (
    <div
      className={classnames('dbh-page-casual-game-competitors', {
        'is-cn': locale === LANGUAGE_CN,
      })}
    >
      <div
        className={classnames('dbh-page-casual-game-competitors-header', {
          'dbh-page-casual-game-competitors-header--mb0': !showFilterTipsText,
        })}
      >
        {/* 顶部标签切换 */}
        <div className="dbh-casual-game-competitors-tabs">
          <div className="dbh-casual-game-competitors-tabs-item">
            <Segment
              className="segment-light"
              options={TAB_OPTIONS_FIRST.map(buildSegmentOption)}
              value={primaryTab}
              onChange={handlePrimaryTabChange}
            />
          </div>
          {hasSecondaryGenreTabs(primaryTab) && (
            <div className="dbh-casual-game-competitors-tabs-item">
              <Segment
                className="segment-light"
                options={getSecondaryTabOptions(primaryTab).map((opt) =>
                  // 二合子 Tab（全部二合 / 剧情二合）不展示悬浮提示
                  buildSegmentOption(opt, { disableBubble: primaryTab === 'merge_2' }),
                )}
                value={secondaryTab}
                onChange={handleSecondaryTabChange}
              />
            </div>
          )}
        </div>
        {/* 筛选器 */}
        <div className="dbh-casual-game-competitors-filter">
          <CasualCompetitorsFilter
            entityType={ENTITY_TYPE}
            loading={regionLoading || defaultTimeLoading}
            regionOptions={regionOptions}
            rangeTimeComponentDefault={latestTimeTypeHasData}
            showRevenueScopeSwitcher={shouldShowRevenueScopeSwitcher}
            value={filterValue}
            onChange={handleFilterChange}
          />
        </div>
        {showFilterTipsText && (
          <div className="dbh-casual-game-competitors-tips">
            {formatElementMessage({ id: 'dbh_studio_casual_tips' })}
          </div>
        )}
      </div>

      {/* 品类大盘模块 */}
      <CategoryOverview
        ref={categoryOverviewRef}
        categoryKpiTipsI18n={categoryKpiTipsI18n}
        params={currentParams}
        dataSource={SENSOR_TOWER_DATA_SOURCE}
      />

      {/* 重点游戏模块 */}
      <KeyGames
        ref={keyGamesRef}
        keyGamesTableTipsI18n={keyGamesTableTipsI18n}
        params={currentParams}
        genreOptionsMap={genreOptionsMap}
        dataSource={SENSOR_TOWER_DATA_SOURCE}
      />

      {/* 头部厂商模块 */}
      <TopPublishersV2
        ref={topPublishersRef}
        topPublishersTableTipsI18n={topPublishersTableTipsI18n}
        topPublisersKpiTipsI18n={topPublisersKpiTipsI18n}
        params={currentParams}
        genreOptionsMap={genreOptionsMap}
        dataSource={SENSOR_TOWER_DATA_SOURCE}
      />

      {/* Footer模块 */}
      <CasualCompetitorsFooter />
    </div>
  );
};

export default CasualCompetitors;
